import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MapPin, Home, ArrowRight, Loader2 } from 'lucide-react';
import { ProjectCard } from '../components/common/ProjectCard';
import { FilterBar } from '../components/common/FilterBar';
import { Badge } from '../components/ui/badge';
import { projectsApi } from '../services/api';

export default function ProjectsPage() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    category: searchParams.get('category') || '',
    status: searchParams.get('status') || '',
    type: searchParams.get('type') || '',
    budget: '',
  });
  const [viewMode, setViewMode] = useState('grid');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      location: searchParams.get('location') || '',
      category: searchParams.get('category') || '',
      status: searchParams.get('status') || '',
      type: searchParams.get('type') || '',
    }));
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.category) params.category = filters.category;
    projectsApi.getAll(params)
      .then(res => setProjects(res.data || res || []))
      .catch(err => {
        setError('Failed to load projects.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [filters.status, filters.category]);

  const filteredProjects = projects.filter((project) => {
    if (filters.location && project.area !== filters.location) return false;
    if (filters.type && project.type !== filters.type) return false;
    return true;
  });

  const statusColors = {
    Ongoing: 'bg-[#47ace2] text-white',
    Completed: 'bg-emerald-500 text-white',
    Upcoming: 'bg-amber-500 text-white',
  };

  return (
    <div data-testid="projects-page" className="pt-20">
      {/* Header */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
            Our Projects
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 font-outfit mt-4">
            Explore Our Portfolio
          </h1>
          <p className="text-lg text-slate-600 font-manrope mt-4 max-w-2xl">
            Discover our range of premium residential and commercial developments 
            across Nagpur's prime locations.
          </p>
        </div>
      </section>

      {/* Filter & Projects */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            viewMode={viewMode}
            setViewMode={setViewMode}
            projects={projects}
          />

          {/* Results Count */}
          <div className="mt-8 mb-6 flex items-center justify-between">
            <p className="text-slate-600 font-manrope">
              Showing <span className="font-semibold text-slate-900">{filteredProjects.length}</span>{' '}
              {filteredProjects.length === 1 ? 'project' : 'projects'}
            </p>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-10 h-10 text-[#47ace2] animate-spin" />
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="text-center py-16">
              <p className="text-red-500 font-manrope">{error}</p>
            </div>
          )}

          {/* Projects Grid/List */}
          {!loading && !error && (filteredProjects.length > 0 ? (
            viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {filteredProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    data-testid={`project-list-${project.id}`}
                    className="flex flex-col md:flex-row gap-6 bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all group"
                  >
                    <div className="relative md:w-80 h-60 md:h-auto flex-shrink-0">
                      <img
                        src={project.images?.[0]}
                        alt={project.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <Badge className={`absolute top-4 left-4 ${statusColors[project.status]}`}>
                        {project.status}
                      </Badge>
                    </div>
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-2xl font-semibold text-slate-900 font-outfit group-hover:text-[#47ace2] transition-colors">
                            {project.name}
                          </h3>
                          <div className="flex items-center gap-1 text-slate-500 mt-2 font-manrope">
                            <MapPin className="w-4 h-4" />
                            {project.location}
                          </div>
                        </div>
                        <Badge variant="secondary" className="flex-shrink-0">
                          {project.category}
                        </Badge>
                      </div>
                      <p className="text-slate-600 font-manrope mt-4 line-clamp-2">
                        {project.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 mt-4">
                        <div className="flex items-center gap-1 text-slate-600 text-sm font-manrope">
                          <Home className="w-4 h-4" />
                          {project.type}
                        </div>
                        {project.configurations?.[0] && <>
                          <span className="text-slate-300">|</span>
                          <span className="text-slate-600 text-sm font-manrope">
                            {project.configurations[0]}
                          </span>
                        </>}
                        {project.total_units > 0 && <>
                          <span className="text-slate-300">|</span>
                          <span className="text-slate-600 text-sm font-manrope">
                            {project.total_units} Units
                          </span>
                        </>}
                      </div>
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                        <p className="text-xl text-[#47ace2] font-semibold font-manrope">
                          {project.price_range || project.priceRange}
                        </p>
                        <span className="flex items-center gap-1 text-slate-500 group-hover:text-[#47ace2] font-medium transition-colors">
                          View Details
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500 font-manrope text-lg">
                No projects found matching your criteria.
              </p>
              <button
                onClick={() =>
                  setFilters({
                    location: '',
                    category: '',
                    status: '',
                    type: '',
                    budget: '',
                  })
                }
                className="mt-4 text-[#47ace2] hover:text-[#1f7db3] font-medium"
              >
                Clear all filters
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
